/**
 * LP設定の型定義と typed re-export
 * ソース: lp-config.yml → lp-config.generated.json
 */
import config from "./lp-config.generated.json";

// --- Leaf types ---

type NavLink = { label: string; href: string };

type CtaButton = { label: string; href: string };

type TrustBadge = { label: string; icon: string };

type MockMatch = { name: string; skill: string; score: number };

type ProblemCard = { image: string; title: string; description: string };

type BeforeAfter = {
  label: string;
  value: string;
  caption: string;
  badge?: string;
};

type SolutionStep = {
  number: number;
  image: string;
  title: string;
  description: string;
};

type ParsedField = { label: string; value: string };

type ScoreItem = { label: string; score: number };

type FeatureEmailMock = { parsedFields: ParsedField[]; piiLabel: string };

type FeatureMatchingMock = {
  resultLabel: string;
  hitCount: string;
  scores: ScoreItem[];
  depthFilter: string;
};

type FeatureProposalMock = {
  to: string;
  subject: string;
  sendButton: string;
};

type FeatureItem = {
  title: string;
  bullets: string[];
  mockData: FeatureEmailMock | FeatureMatchingMock | FeatureProposalMock;
};

type KpiCard = { value: string; label: string; sublabel: string };

type ImpactComparisonRow = {
  feature: string;
  before: string;
  after: string;
  effect: string;
};

type SliderConfig = {
  label: string;
  min: number;
  max: number;
  step: number;
  unit: string;
};

type ComparisonRow = {
  feature: string;
  ratings: string[];
  isCategory?: boolean;
};

type CoverageBarColor = "green" | "orange" | "blue" | "red";

type PipelineCoverageItem = {
  name: string;
  percent: number;
  color: CoverageBarColor;
  note?: string;
};

type OnboardingStep = { day: string; title: string; description: string };

type Testimonial = {
  company: string;
  person: string;
  quote: string;
  result: string;
};

type PricingPlan = {
  name: string;
  monthlyPrice: number | null;
  priceLabel?: string;
  description: string;
  isPopular: boolean;
  accentColor: string;
  perUserPrice: number | null;
  userLimit: string;
  showSwitchBadge?: boolean;
  features: string[];
};

type FaqItem = { question: string; answer: string };

type SecurityCard = { icon: string; title: string; description: string };

type FooterLink = { label: string; href: string };

type FooterColumn = { heading: string; links: FooterLink[] };

// --- Top-level config ---

type LpConfig = {
  meta: {
    title: string;
    description: string;
    keywords: string;
    ogTitle: string;
    ogDescription: string;
  };
  navbar: { links: NavLink[]; ctaLabel: string };
  hero: {
    badge: string;
    headline: string;
    subheadline: string;
    description: string[];
    ctaPrimary: CtaButton;
    ctaSecondary: CtaButton;
    trustBadges: TrustBadge[];
    mockMatchBadge: string;
    mockMatches: MockMatch[];
  };
  logoBand: { heading: string; companies: string[] };
  problem: {
    sectionLabel: string;
    heading: string;
    cards: ProblemCard[];
    before: BeforeAfter;
    after: BeforeAfter;
  };
  solution: {
    sectionLabel: string;
    heading: string;
    steps: SolutionStep[];
  };
  features: {
    sectionLabel: string;
    heading: string;
    items: FeatureItem[];
  };
  impact: {
    sectionLabel: string;
    heading: string;
    kpiCards: KpiCard[];
    comparisonTable: {
      headers: { before: string; after: string; effect: string };
      rows: ImpactComparisonRow[];
    };
  };
  roiCalculator: {
    sectionLabel: string;
    heading: string;
    subtitle: string;
    defaults: {
      staffCount: number;
      dailyHours: number;
      hourlyRate: number;
      marginPerDeal: number;
    };
    sliders: {
      staffCount: SliderConfig;
      dailyHours: SliderConfig;
      hourlyRate: SliderConfig;
      marginPerDeal: SliderConfig;
    };
    reductionRate: number;
    workingDaysPerMonth: number;
    workingHoursPerDay: number;
    currentDealsPerPerson: number;
    labels: {
      costReduction: string;
      monthlySavedHours: string;
      yearlyCostReduction: string;
      profitIncrease: string;
      dealsPerPerson: string;
      additionalDeals: string;
      yearlyProfitIncrease: string;
      yearlyTotal: string;
      lossAversionPrefix: string;
      lossAversionSuffix: string;
    };
  };
  comparisonTable: {
    sectionLabel: string;
    heading: string;
    subtitle: string;
    footnote: string;
    pipelineSteps: string[];
    competitors: string[];
    pipelineCoverage: PipelineCoverageItem[];
    rows: ComparisonRow[];
  };
  onboarding: {
    sectionLabel: string;
    heading: string;
    subtitle: string;
    steps: OnboardingStep[];
  };
  testimonials: {
    sectionLabel: string;
    heading: string;
    items: Testimonial[];
  };
  pricing: {
    sectionLabel: string;
    heading: string;
    subtitle: string;
    monthlyLabel: string;
    annualLabel: string;
    annualDiscount: number;
    popularBadge: string;
    ctaWithPrice: string;
    ctaFree: string;
    ctaWithoutPrice: string;
    priceSuffix: string;
    perUserLabel: string;
    perUserBestValue: string;
    annualSavingsPrefix: string;
    annualSavingsSuffix: string;
    lossAversionMessage: string;
    switchBadge: string;
    plans: PricingPlan[];
  };
  faq: { sectionLabel: string; heading: string; items: FaqItem[] };
  security: {
    sectionLabel: string;
    heading: string;
    cards: SecurityCard[];
  };
  ctaForm: {
    heading: string;
    description: string;
    trustItems: string[];
    teamSizeOptions: string[];
    submitLabel: string;
    submittingLabel: string;
    successTitle: string;
    successMessage: string;
    errorMessage: string;
  };
  footer: {
    tagline: string;
    copyright: string;
    columns: FooterColumn[];
  };
  floatingCta: { label: string; href: string };
};

/**
 * LP設定オブジェクト（型付き）
 * @return {LpConfig} LP設定
 */
export const lpConfig = config as unknown as LpConfig;

export type {
  LpConfig,
  NavLink,
  TrustBadge,
  MockMatch,
  ProblemCard,
  BeforeAfter,
  SolutionStep,
  FeatureItem,
  FeatureEmailMock,
  FeatureMatchingMock,
  FeatureProposalMock,
  KpiCard,
  ImpactComparisonRow,
  SliderConfig,
  ComparisonRow,
  CoverageBarColor,
  PipelineCoverageItem,
  OnboardingStep,
  Testimonial,
  PricingPlan,
  FaqItem,
  SecurityCard,
  FooterColumn,
};
